import { updatedDiff } from "deep-object-diff"
import PropTypes from "prop-types"
import { createContext, useContext, useEffect, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import { addClientCourses, updateClient } from "~/actions/clients"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"
import serialize from "~/store/serialize"
import differenceBetween from "~/utils/differenceBetween"

import equalAndSameOrder from "~/utils/equalAndSameOrder"
import AppContext from "./App"
const ClientContext = createContext()
export default ClientContext

export const ClientContainer = ({ children, client }) => {
  const queryClient = useQueryClient()
  const { openSnackBar } = useContext(AppContext)
  //state to check if there is data unsaved
  const [unsavedState, setUnsavedState] = useState([])
  //state to show the validation errors for client name
  const [lengthError, setLengthError] = useState(false)
  //state to show the validation errors if the user try to save client
  const [tryToSave, setTryToSave] = useState(false)
  // color picker visibility state
  const [showPicker, setShowPicker] = useState(false)
  //client general information state with debounced
  const [debouncedDetails, setDebouncedDetails] = useState({
    name: "",
  })
  const openErrorSnackbar = useOpenErrorSnackbar()
  //loading state to save
  const [loading, setLoading] = useState(false)

  //client general information state

  const [clientDetailsValues, setClientDetailsValues] = useState({
    name: client?.name,
    domain: "",
    brand_color: "",
    user_responses: false,
    user_stats: false,
    time_to_proficiency: false,
    kc_and_final: false,
    sentiment_evaluation: false,
    cohort_course_analysis: false,
    persona_insight: false,
    dashboard_visible: false,
    dashboard_description: "",
    dashboard_video: null,
    dashboard_image: null,
    stats_users_only: false,
    use_client_courses_for_stats_endpoint: false,
    include_questions_answer_client_stats: false,
    stats_endpoint_key: null,
    app_subdomain: null,
    app_theme: null,
    login_url: null,
  })

  //client general information initial state
  const [initialDetailsContext, setInitialDetailsContext] = useState({
    name: client?.name,
    domain: "",
    brand_color: "",
    user_responses: false,
    user_stats: false,
    time_to_proficiency: false,
    kc_and_final: false,
    sentiment_evaluation: false,
    cohort_course_analysis: false,
    persona_insight: false,
    dashboard_visible: false,
    dashboard_description: "",
    dashboard_video: null,
    dashboard_image: null,
    stats_users_only: false,
    use_client_courses_for_stats_endpoint: false,
    include_questions_answer_client_stats: false,
    stats_endpoint_key: null,
    app_subdomain: null,
    app_theme: null,
    login_url: null,
  })

  const [dashboardCourses, setDashboardCourses] = useState([])
  const [initialDashboardCourses, setInitialDashboardCourses] = useState([])
  const [dashboardLearningPaths, setDashboardLearningPaths] = useState([])
  const [initialDashboardLearningPaths, setInitialDashboardLearningPaths] =
    useState([])

  //check unsaved details data
  useEffect(() => {
    const unsavedData = updatedDiff(initialDetailsContext, clientDetailsValues)

    if (
      Object.keys(unsavedData)?.length > 0 &&
      !unsavedState.includes("details")
    ) {
      setUnsavedState((oldState) => [...oldState, "details"])
    } else if (
      Object.keys(unsavedData)?.length === 0 &&
      unsavedState.includes("details")
    ) {
      setUnsavedState(
        unsavedState.filter((x) => {
          return x !== "details"
        }),
      )
    }
  }, [clientDetailsValues, initialDetailsContext])

  //add courses to a client state
  const [courseArray, setCourseArray] = useState([])

  //load data in case of the refresth
  useEffect(() => {
    if (client?.courses?.length > 0) {
      let newArr = []
      newArr = client.courses.map((c) => c)

      setCourseArray(newArr)
    }
  }, [client])

  //check un save in adding courses
  useEffect(() => {
    const initialValues =
      client?.courses?.map((c) => Number(c.container_id)) || []
    const currentValues = courseArray.map((c) => Number(c.container_id))

    const changes = differenceBetween(initialValues, currentValues)
    if (changes.length > 0 && !unsavedState.includes("courses")) {
      setUnsavedState((oldState) => [...oldState, "courses"])
    } else if (changes.length === 0 && unsavedState.includes("courses")) {
      setUnsavedState(
        unsavedState.filter((x) => {
          return x !== "courses"
        }),
      )
    }
  }, [JSON.stringify(client), courseArray])

  //check un save in adding dashboard courses
  useEffect(() => {
    const initialValues =
      initialDashboardCourses?.map((c) => Number(c.container_id)) || []
    const currentValues = dashboardCourses.map((c) => Number(c.container_id))
    const changes = differenceBetween(initialValues, currentValues)
    const sameOrder = equalAndSameOrder(initialValues, currentValues)

    if (
      (changes.length > 0 || !sameOrder) &&
      !unsavedState.includes("dashboardCourses")
    ) {
      setUnsavedState((oldState) => [...oldState, "dashboardCourses"])
    } else if (
      sameOrder &&
      changes.length === 0 &&
      unsavedState.includes("dashboardCourses")
    ) {
      setUnsavedState(
        unsavedState.filter((x) => {
          return x !== "dashboardCourses"
        }),
      )
    }
  }, [JSON.stringify(client), dashboardCourses])

  //check un save in adding dashboard learning paths
  useEffect(() => {
    const initialValues =
      initialDashboardLearningPaths?.map((c) => Number(c.id)) || []
    const currentValues = dashboardLearningPaths.map((c) => Number(c.id))
    const changes = differenceBetween(initialValues, currentValues)
    const sameOrder = equalAndSameOrder(initialValues, currentValues)

    if (
      (changes.length > 0 || !sameOrder) &&
      !unsavedState.includes("dashboardPaths")
    ) {
      setUnsavedState((oldState) => [...oldState, "dashboardPaths"])
    } else if (
      sameOrder &&
      changes.length === 0 &&
      unsavedState.includes("dashboardPaths")
    ) {
      setUnsavedState(
        unsavedState.filter((x) => {
          return x !== "dashboardPaths"
        }),
      )
    }
  }, [JSON.stringify(client), dashboardLearningPaths])

  const disabledSave =
    clientDetailsValues?.name === "" || clientDetailsValues?.brand_color === ""

  //mutations

  const updateClientMutation = useMutation((data) =>
    updateClient(data, client?.id),
  )
  const addClientsCoursesMutation = useMutation((data) =>
    addClientCourses(data, client?.id),
  )

  const updateClientDetails = () => {
    return new Promise((resolve, reject) => {
      if (
        unsavedState.includes("details") ||
        unsavedState.includes("dashboardCourses") ||
        unsavedState.includes("dashboardPaths")
      ) {
        const orderedDashboardCourses = dashboardCourses.map((c, i) => ({
          course_container_id: c.container_id,
          order: i,
        }))
        const orderedDashboardLearningPaths = dashboardLearningPaths.map(
          (lp, i) => ({
            learning_path_id: lp.id,
            order: i,
          }),
        )
        const data = {
          name: clientDetailsValues?.name,
          domain: clientDetailsValues?.domain,
          brand_color: clientDetailsValues?.brand_color,
          user_responses: clientDetailsValues?.user_responses,
          user_stats: clientDetailsValues?.user_stats,
          time_to_proficiency: clientDetailsValues?.time_to_proficiency,
          kc_and_final: clientDetailsValues?.kc_and_final,
          sentiment_evaluation: clientDetailsValues?.sentiment_evaluation,
          cohort_course_analysis: clientDetailsValues?.cohort_course_analysis,
          persona_insight: clientDetailsValues?.persona_insight,
          logo: client?.logo ? JSON.stringify(client?.logo) : null,
          certification_logo: client?.certification_logo,
          secret_saml_key:
            clientDetailsValues?.secret_saml_key === ""
              ? null
              : clientDetailsValues?.secret_saml_key,
          saml_idp_name:
            clientDetailsValues?.saml_idp_name === ""
              ? null
              : clientDetailsValues?.saml_idp_name,
          dashboard_visible: clientDetailsValues?.dashboard_visible,
          dashboard_description: clientDetailsValues?.dashboard_description,
          dashboard_video: clientDetailsValues?.dashboard_video,
          dashboard_image: clientDetailsValues?.dashboard_image,
          stats_users_only: clientDetailsValues?.stats_users_only,
          use_client_courses_for_stats_endpoint:
            clientDetailsValues?.use_client_courses_for_stats_endpoint,
          include_questions_answer_client_stats:
            clientDetailsValues?.include_questions_answer_client_stats,
          stats_endpoint_key:
            clientDetailsValues?.stats_endpoint_key === ""
              ? null
              : clientDetailsValues?.stats_endpoint_key,
          dashboard_courses: orderedDashboardCourses,
          dashboard_learning_paths: orderedDashboardLearningPaths,
          app_subdomain: clientDetailsValues?.app_subdomain,
          app_theme: clientDetailsValues?.app_theme,
          login_url: clientDetailsValues?.login_url,
        }
        updateClientMutation.mutate(data, {
          onSuccess: (res) => {
            serialize("client", res.data).then((serializedData) => {
              return queryClient.setQueryData("clients", (oldData) => {
                let newArray = []
                newArray = oldData.map((x) => {
                  if (x.id !== serializedData.id) {
                    return x
                  } else {
                    return serializedData
                  }
                })
                return newArray
              })
            })
            resolve()
            setShowPicker(false)
          },
          onError: (err) => {
            openErrorSnackbar(err, "Error. Failed to update Client")

            setLoading(false)
            reject()
          },
        })
      } else {
        resolve()
      }
    })
  }

  // add courses to client
  const addClientCoursesFunc = () => {
    return new Promise((resolve, reject) => {
      if (unsavedState.includes("courses")) {
        const data = courseArray.map((c) => {
          return c.container_id
        })
        addClientsCoursesMutation.mutate(data, {
          onSuccess: (res) => {
            serialize("client", res.data).then((serializedData) => {
              return queryClient.setQueryData("clients", (oldData) => {
                let newData = oldData.map((c) => {
                  if (c.id === client?.id) {
                    return serializedData
                  } else {
                    return c
                  }
                })
                return newData
              })
            })
            resolve()
          },
          onError: (res) => {
            openErrorSnackbar(res, "Error Updating Client")
            setLoading(false)
            reject()
          },
        })
      } else {
        resolve()
      }
    })
  }
  const updateClientFunc = () => {
    if (disabledSave) {
      setTryToSave(true)
    } else {
      setLoading(true)
      setTryToSave(false)
      return updateClientDetails()
        .then(addClientCoursesFunc)
        .then(() => {
          setLoading(false)
          openSnackBar({
            message: "Client Updated",
          })
        })
        .catch((err) => {
          setLoading(false)
          openSnackBar({
            message: "Error Updating Client",
          })
        })
    }
  }

  //discard changes
  const discardChanges = () => {
    if (unsavedState.includes("details")) {
      setClientDetailsValues(initialDetailsContext)
      setDebouncedDetails(initialDetailsContext.name)
    } else if (unsavedState.includes("courses")) {
      setCourseArray(client?.courses)
    } else if (unsavedState.includes("dashboardCourses")) {
      setDashboardCourses(initialDashboardCourses)
    } else if (unsavedState.includes("dashboardPaths")) {
      setDashboardLearningPaths(initialDashboardLearningPaths)
    }
  }

  return (
    <ClientContext.Provider
      value={{
        client,
        debouncedDetails,
        setDebouncedDetails,
        clientDetailsValues,
        setClientDetailsValues,
        initialDetailsContext,
        setInitialDetailsContext,
        lengthError,
        setLengthError,
        tryToSave,
        setTryToSave,
        unsavedState,
        setUnsavedState,
        updateClientFunc,
        updateClientMutation,
        disabledSave,
        courseArray,
        setCourseArray,
        loading,
        setLoading,
        discardChanges,
        showPicker,
        setShowPicker,
        dashboardCourses,
        setDashboardCourses,
        dashboardLearningPaths,
        setDashboardLearningPaths,
        setInitialDashboardCourses,
        setInitialDashboardLearningPaths,
      }}
    >
      {children}
    </ClientContext.Provider>
  )
}

ClientContainer.propTypes = {
  children: PropTypes.node,
  client: PropTypes.object,
}
